import axios, {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosRequestHeaders,
    AxiosResponse,
} from 'axios';
import {
    apiErrorsToConsolidatedErrorObject,
    generateApiUnexpectedError,
} from './utils/data-management';
import { Session } from '../contexts/session';
import { apiError, APIGetPaginatedParams } from './types';
import { PollData, PollRecord, PollStatus } from '../models/Poll';

// API page limit for pagination. Max number of elements to retrieve per request
export const PollAPIPageSize = 20;

interface GetManyReturnType {
    count: number;
    data: PollRecord[];
    lastKey?: {
        [key: string]: any;
    };
}

enum PollAPIPaths {
    Polls = '/polls',
    MyPolls = '/mypolls',
}

interface PollAPIGetMyPollsParams extends APIGetPaginatedParams {
    status?: PollStatus;
}

interface PollAPIPatchMyPollData {
    status?: PollStatus;
}
class PollAPI {
    private static getPollAPIAxiosObj = (
        session?: Session,
        path?: string,
    ): AxiosInstance => {
        const headers: AxiosRequestHeaders = {
            'Content-Type': 'application/json',
        };

        if (session) {
            headers.Authorization = `Bearer ${session.idToken}`;
        }

        const baseURL = path
            ? process.env.REACT_APP_POLL_API_BASEURL?.replace(
                  PollAPIPaths.Polls,
                  path,
              )
            : process.env.REACT_APP_POLL_API_BASEURL;

        return axios.create({
            baseURL,
            headers,
        });
    };

    public static delete = async (
        pollId: string,
        session: Session,
    ): Promise<apiError | undefined> => {
        const pollAPI = PollAPI.getPollAPIAxiosObj(session);

        try {
            await pollAPI.delete(`/${pollId}`);
        } catch (err: any) {
            let error: apiError;
            const { status, data } = err.response;

            // Validation error
            if (status === 400 && data instanceof Array && data.length) {
                error = apiErrorsToConsolidatedErrorObject(data);
            } else {
                error = generateApiUnexpectedError('deleting poll');
            }

            return error;
        }
    };

    public static create = async (
        data: PollData,
        session: Session,
    ): Promise<apiError | undefined> => {
        const pollAPI = PollAPI.getPollAPIAxiosObj(session);

        try {
            await pollAPI.post('', data);
        } catch (err: unknown) {
            let error: apiError;

            if (axios.isAxiosError(err) && err.response) {
                const { status, data } = err.response;

                // Validation error
                if (status === 400 && data instanceof Array && data.length) {
                    error = apiErrorsToConsolidatedErrorObject(data);
                } else {
                    error = generateApiUnexpectedError('creating poll');
                }

                return error;
            }
        }
    };

    public static getMany = async (
        params?: APIGetPaginatedParams,
    ): Promise<GetManyReturnType> => {
        const pollAPI = PollAPI.getPollAPIAxiosObj();

        const limit = params?.scanLimit ?? PollAPIPageSize.toString();
        const config: AxiosRequestConfig<APIGetPaginatedParams> = {
            params: {
                scanLimit: limit.toString(),
            },
        };

        if (config.params && params?.lastKey) {
            config.params.lastKey = JSON.stringify(params?.lastKey);
        }

        const { status, data } = await pollAPI.get<
            GetManyReturnType,
            AxiosResponse<GetManyReturnType>,
            APIGetPaginatedParams
        >('', config);

        if (status === 200 && data) {
            return data;
        } else {
            throw new Error('Error retrieving polls');
        }
    };

    public static getOne = async (id: string): Promise<PollRecord> => {
        const pollAPI = PollAPI.getPollAPIAxiosObj();

        const { status, data } = await pollAPI.get<PollRecord>(`/${id}`);

        if (status === 200 && data) {
            return data;
        } else {
            throw new Error('Error retrieving poll');
        }
    };

    public static getMyPolls = async (
        session: Session,
        params?: APIGetPaginatedParams,
    ): Promise<GetManyReturnType> => {
        const pollAPI = PollAPI.getPollAPIAxiosObj(
            session,
            PollAPIPaths.MyPolls,
        );

        // const config: AxiosRequestConfig<PollAPIGetMyPollsParams> = {};

        const limit = params?.scanLimit ?? PollAPIPageSize.toString();
        const config: AxiosRequestConfig<APIGetPaginatedParams> = {
            params: {
                scanLimit: limit.toString(),
            },
        };

        if (config.params && params?.lastKey) {
            config.params.lastKey = JSON.stringify(params?.lastKey);
        }

        const { status, data } = await pollAPI.get<
            GetManyReturnType,
            AxiosResponse<GetManyReturnType>,
            PollAPIGetMyPollsParams
        >('', config);

        if (status === 200 && data) {
            return data;
        } else {
            throw new Error('Error retrieving polls');
        }
    };

    public static patchMyPoll = async (
        session: Session,
        pollId: string,
        pollData: PollAPIPatchMyPollData,
    ): Promise<GetManyReturnType> => {
        const pollAPI = PollAPI.getPollAPIAxiosObj(
            session,
            PollAPIPaths.MyPolls,
        );

        const { status, data } = await pollAPI.patch(`/${pollId}`, pollData);

        if (status === 200 && data) {
            return data;
        } else {
            throw new Error('Error patching poll');
        }
    };

    public static createReport = async (
        pollId: string,
        data: {
            reason: string;
        },
        session: Session,
    ): Promise<apiError | void> => {
        let error: apiError = generateApiUnexpectedError(
            'creating poll report',
        );
        const pollAPI = PollAPI.getPollAPIAxiosObj(session);

        try {
            const result = await pollAPI.post(`/${pollId}/reports`, data);

            if (result.status === 200) {
                return;
            }
        } catch (err: unknown) {
            if (axios.isAxiosError(err) && err.response) {
                const { status, data } = err.response;

                // Validation error
                if (status === 400 && data instanceof Array && data.length) {
                    error = apiErrorsToConsolidatedErrorObject(data);
                }
            }
        }

        return error;
    };
}

export default PollAPI;
